// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/SubscriptionDataSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import { CloudOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SubscriptionDataSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const sub = data?.subscriptionData;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = sub && (sub.billingCycle || sub.planTier || sub.features?.length || sub.platform);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const formatCurrency = value => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['subscription-info']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'subscription-info',
          label: (
            <Flex align="center" gap={8}>
              <CloudOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('subscription_info')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {sub.platform && (
                <Descriptions.Item label={translateX('platform')}>
                  {sub.platform}
                </Descriptions.Item>
              )}
              {sub.planTier && (
                <Descriptions.Item label={translateX('plan_tier')}>
                  <Tag color="blue">{sub.planTier}</Tag>
                </Descriptions.Item>
              )}
              {sub.billingCycle && (
                <Descriptions.Item label={translateX('billing_cycle')}>
                  <Tag>{sub.billingCycle}</Tag>
                </Descriptions.Item>
              )}
              {sub.trialDays !== undefined && sub.trialDays !== null && (
                <Descriptions.Item label={translateX('trial_days')}>
                  {sub.trialDays} {translateX('days')}
                </Descriptions.Item>
              )}
              {sub.setupFee !== undefined && sub.setupFee !== null && (
                <Descriptions.Item label={translateX('setup_fee')}>
                  {formatCurrency(sub.setupFee)}
                </Descriptions.Item>
              )}
              {sub.includedUsers !== undefined && sub.includedUsers !== null && (
                <Descriptions.Item label={translateX('included_users')}>
                  {sub.includedUsers}
                </Descriptions.Item>
              )}
              {sub.additionalUserPrice !== undefined && sub.additionalUserPrice !== null && (
                <Descriptions.Item label={translateX('additional_user_price')}>
                  {formatCurrency(sub.additionalUserPrice)}
                </Descriptions.Item>
              )}
              {sub.storageIncludedGb !== undefined && sub.storageIncludedGb !== null && (
                <Descriptions.Item label={translateX('storage_included')}>
                  {sub.storageIncludedGb} GB
                </Descriptions.Item>
              )}
              {sub.contractMonths !== undefined && sub.contractMonths !== null && (
                <Descriptions.Item label={translateX('contract_months')}>
                  {sub.contractMonths} {translateX('months')}
                </Descriptions.Item>
              )}
              {sub.autoRenewal !== undefined && (
                <Descriptions.Item label={translateX('auto_renewal')}>
                  {sub.autoRenewal ? (
                    <Tag color="green">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="red">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
              {sub.onboardingIncluded !== undefined && (
                <Descriptions.Item label={translateX('onboarding_included')}>
                  {sub.onboardingIncluded ? (
                    <Tag color="green">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="red">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
              {sub.supportLevel && (
                <Descriptions.Item label={translateX('support_level')}>
                  <Tag color={sub.supportLevel === '24x7' ? 'green' : sub.supportLevel === 'phone' ? 'blue' : 'default'}>
                    {sub.supportLevel}
                  </Tag>
                </Descriptions.Item>
              )}
              {sub.sla && (
                <Descriptions.Item label="SLA">
                  {sub.sla}
                </Descriptions.Item>
              )}
              {sub.cancellationPolicy && (
                <Descriptions.Item label={translateX('cancellation_policy')}>
                  {sub.cancellationPolicy}
                </Descriptions.Item>
              )}
              {sub.features?.length > 0 && (
                <Descriptions.Item label={translateX('features')}>
                  <Flex wrap="wrap" gap={4}>
                    {sub.features.map((feat, i) => (
                      <Tag key={i} color="blue" style={{ margin: 0 }}>{feat}</Tag>
                    ))}
                  </Flex>
                </Descriptions.Item>
              )}
              {sub.integrations?.length > 0 && (
                <Descriptions.Item label={translateX('integrations')}>
                  <Flex wrap="wrap" gap={4}>
                    {sub.integrations.map((intg, i) => (
                      <Tag key={i} style={{ margin: 0 }}>{intg}</Tag>
                    ))}
                  </Flex>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SubscriptionDataSection;
